import { useMemo, useState } from "react";
import {
  CheckCircle2,
  Eye,
  EyeOff,
  Globe,
  KeyRound,
  Lock,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { cn } from "../../lib/cn";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import { Input, Textarea } from "../ui/Input";
import { ApiError, updateConnectionConfig } from "../../lib/api";
import type { ConnectionConfig, ParamSpec } from "../../lib/api";

const selectBase =
  "w-full appearance-none rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-soft transition-colors focus:border-accent-400 focus-ring";

function labelOf(p: ParamSpec): string {
  if (p.label) return p.label;
  return p.name.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Dynamic fields: a param is visible only while every `visible_when`
 * dependency (e.g. auth_method) matches the current values. */
function isVisible(p: ParamSpec, values: Record<string, string>): boolean {
  for (const [dep, allowed] of Object.entries(p.visible_when ?? {})) {
    if (!allowed.includes(values[dep] ?? "")) return false;
  }
  return true;
}

/** Group visible params preserving schema order; unnamed groups → "General". */
function grouped(schema: ParamSpec[], values: Record<string, string>): [string, ParamSpec[]][] {
  const order: string[] = [];
  const byGroup = new Map<string, ParamSpec[]>();
  for (const p of schema) {
    if (!isVisible(p, values)) continue;
    const g = p.group || "General";
    if (!byGroup.has(g)) {
      byGroup.set(g, []);
      order.push(g);
    }
    byGroup.get(g)!.push(p);
  }
  return order.map((g) => [g, byGroup.get(g)!]);
}

function Toggle({
  checked,
  onChange,
  id,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  id: string;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cn(
        "focus-ring relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
        checked ? "bg-brand-600" : "bg-slate-200",
      )}
    >
      <span
        className={cn(
          "inline-block h-[18px] w-[18px] transform rounded-full bg-white shadow transition-transform",
          checked ? "translate-x-[24px]" : "translate-x-[3px]",
        )}
      />
    </button>
  );
}

function SecretInput({
  p,
  value,
  isSet,
  error,
  onChange,
}: {
  p: ParamSpec;
  value: string;
  isSet: boolean;
  error?: string;
  onChange: (v: string) => void;
}) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <div className="relative">
        <KeyRound className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          id={`param-${p.name}`}
          type={show ? "text" : "password"}
          autoComplete="new-password"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={isSet ? "••••••••  (stored in Vault)" : p.placeholder || "Enter secret"}
          className={cn("pl-10 pr-20", error && "border-rose-400")}
        />
        <div className="absolute right-2.5 top-1/2 flex -translate-y-1/2 items-center gap-1.5">
          {isSet && !value && (
            <Badge tone="green" className="px-2 py-0.5">
              <Lock className="h-3 w-3" /> Set
            </Badge>
          )}
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="focus-ring rounded-lg p-1 text-slate-400 hover:text-slate-600"
            aria-label={show ? "Hide value" : "Show value"}
          >
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {isSet && (
        <p className="mt-1 text-xs text-slate-400">
          Leave blank to keep the stored value · secrets live in Vault, never in the config XML.
        </p>
      )}
    </div>
  );
}

export function ConfigForm({
  tenant,
  slug,
  systemType,
  config,
  onSaved,
}: {
  tenant: string;
  slug: string;
  systemType: string;
  config: ConnectionConfig;
  onSaved: (cfg: ConnectionConfig) => void;
}) {
  const initial = useMemo(() => {
    const v: Record<string, string> = {};
    for (const p of config.schema) {
      if (p.secret) {
        v[p.name] = "";
      } else {
        v[p.name] = config.values[p.name] ?? p.default ?? "";
      }
    }
    return v;
  }, [config]);

  const [values, setValues] = useState<Record<string, string>>(initial);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [savedVersion, setSavedVersion] = useState<number | null>(null);

  const dirty = useMemo(
    () => Object.keys(values).some((k) => values[k] !== initial[k]),
    [values, initial],
  );

  function set(name: string, v: string) {
    setValues((prev) => ({ ...prev, [name]: v }));
    setFieldErrors((prev) => {
      if (!(name in prev)) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setSavedVersion(null);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setBanner(null);
    setFieldErrors({});
    try {
      const cfg = await updateConnectionConfig(tenant, slug, values);
      setSavedVersion(cfg.config_version);
      // Clear secret inputs (they're now stored) and resync non-secrets.
      setValues((prev) => {
        const next = { ...prev };
        for (const p of config.schema) if (p.secret) next[p.name] = "";
        return next;
      });
      onSaved(cfg);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.fields) setFieldErrors(err.fields);
        setBanner(err.message);
      } else {
        setBanner("Couldn't reach the server");
      }
    } finally {
      setSaving(false);
    }
  }

  if (config.schema.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 px-6 py-10 text-center">
        <Sparkles className="mx-auto h-6 w-6 text-slate-300" />
        <p className="mt-3 text-sm font-semibold text-slate-700">No parameter schema yet</p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-slate-400">
          This connector doesn't declare configuration parameters. Recreate it from a prompt to
          get an AI-generated schema, or use a standard system type.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit}>
      {config.schema_source === "preset" && (
        <div className="mb-5 flex items-start gap-2.5 rounded-xl bg-brand-50/70 px-4 py-3 text-sm text-brand-800 ring-1 ring-inset ring-brand-100">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Using the standard <span className="font-semibold">{systemType}</span> parameter set —
            this connector predates AI-generated schemas.
          </span>
        </div>
      )}

      {banner && (
        <div className="mb-5 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 ring-1 ring-inset ring-rose-100">
          {banner}
        </div>
      )}

      <div className="space-y-8">
        {grouped(config.schema, values).map(([group, params]) => (
          <fieldset key={group}>
            <legend className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
              {group.toLowerCase().includes("auth") ? (
                <ShieldCheck className="h-3.5 w-3.5" />
              ) : (
                <Globe className="h-3.5 w-3.5" />
              )}
              {group}
            </legend>
            <div className="mt-3 grid gap-5 sm:grid-cols-2">
              {params.map((p) => {
                const error = fieldErrors[p.name];
                const wide = p.kind === "multiline" || p.kind === "url" || p.secret;
                return (
                  <div key={p.name} className={cn(wide && "sm:col-span-2")}>
                    <label
                      htmlFor={`param-${p.name}`}
                      className="mb-1.5 block text-sm font-semibold text-slate-700"
                    >
                      {labelOf(p)}
                      {p.required && <span className="ml-0.5 text-rose-400">*</span>}
                    </label>

                    {p.secret ? (
                      <SecretInput
                        p={p}
                        value={values[p.name] ?? ""}
                        isSet={config.secrets_set.includes(p.name)}
                        error={error}
                        onChange={(v) => set(p.name, v)}
                      />
                    ) : p.kind === "enum" ? (
                      <div className="relative">
                        <select
                          id={`param-${p.name}`}
                          value={values[p.name] ?? ""}
                          onChange={(e) => set(p.name, e.target.value)}
                          className={cn(selectBase, error && "border-rose-400")}
                        >
                          {!p.required && <option value="">—</option>}
                          {p.choices.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                        <svg
                          className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    ) : p.kind === "boolean" ? (
                      <div className="flex h-11 items-center gap-3">
                        <Toggle
                          id={`param-${p.name}`}
                          checked={(values[p.name] ?? "") === "true"}
                          onChange={(v) => set(p.name, v ? "true" : "false")}
                        />
                        <span className="text-sm text-slate-500">
                          {(values[p.name] ?? "") === "true" ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    ) : p.kind === "multiline" ? (
                      <Textarea
                        id={`param-${p.name}`}
                        rows={4}
                        value={values[p.name] ?? ""}
                        onChange={(e) => set(p.name, e.target.value)}
                        placeholder={p.placeholder}
                        className={cn(error && "border-rose-400")}
                      />
                    ) : (
                      <div className="relative">
                        {p.kind === "url" && (
                          <Globe className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        )}
                        <Input
                          id={`param-${p.name}`}
                          type={p.kind === "number" ? "number" : "text"}
                          value={values[p.name] ?? ""}
                          onChange={(e) => set(p.name, e.target.value)}
                          placeholder={p.placeholder}
                          className={cn(p.kind === "url" && "pl-10", error && "border-rose-400")}
                        />
                      </div>
                    )}

                    {error ? (
                      <p className="mt-1 text-xs font-medium text-rose-600">{error}</p>
                    ) : (
                      p.description && <p className="mt-1 text-xs text-slate-400">{p.description}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </fieldset>
        ))}
      </div>

      <div className="mt-8 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-center gap-1.5 text-xs text-slate-400">
          <ShieldCheck className="h-3.5 w-3.5" />
          Saved as Ed25519-signed XML (v{config.config_version}) · secrets go to Vault
        </p>
        <div className="flex items-center gap-3">
          {savedVersion !== null && (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600">
              <CheckCircle2 className="h-4 w-4" /> Saved — config v{savedVersion} signed
            </span>
          )}
          <Button type="submit" loading={saving} disabled={!dirty && savedVersion === null}>
            Save configuration
          </Button>
        </div>
      </div>
    </form>
  );
}
